import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { ApiConnector } from "../../api/api-connector";
import { Tooltip } from "../../components/ui-kit/base/tooltip";
import { Button } from "../../components/ui-kit/button";
import { Dialog } from "../../components/ui-kit/dialog";
import { useDialog } from "../../components/ui-kit/dialog-provider";
import { hasAdminAccess } from "../../constants/permissions";
import { Patient } from "./patient";

export const UserManagement = observer(function UserManagement(props: {
  patient: Patient;
  api: ApiConnector;
}) {
  const { alert } = useDialog();
  const { t } = useLingui();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const connectedUsers = props.patient.data?.connected_users ?? [];

  const canManagePasswords = hasAdminAccess(props.api.permissions);

  if (connectedUsers.length === 0) {
    return (
      <div className="text-muted-foreground">
        <Trans>No connected user.</Trans>
      </div>
    );
  }

  return (
    <>
      {connectedUsers.map((user) => (
        <div key={user.uuid} className="py-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 font-medium wrap-break-word">
              {user.username}
            </span>
            {canManagePasswords && (
              <div className="flex flex-wrap gap-2">
                <Tooltip content={t`Send 'forgot password' reset email.`}>
                  <Button
                    variant="subtle"
                    onClick={async () => {
                      await props.patient.resetConnectedUserPassword(user.uuid);
                      await alert(t`Reset email was sent.`);
                    }}
                  >
                    <Trans>Reset password</Trans>
                  </Button>
                </Tooltip>
                <Tooltip
                  content={t`Generate a one-time temporary password (valid 2 days).`}
                >
                  <Button
                    variant="subtle"
                    onClick={async () => {
                      const password =
                        await props.patient.issueTemporaryPassword(user.uuid);
                      setTempPassword(password);
                    }}
                  >
                    <Trans>Temp. password</Trans>
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ))}

      <Dialog
        open={tempPassword !== null}
        onOpenChange={(open) => {
          if (!open) setTempPassword(null);
        }}
      >
        <p className="mt-4 mb-4">
          <Trans>Temporary password for patient:</Trans>
        </p>
        <p className="mt-4 mb-4 font-mono text-lg font-bold tracking-wide">
          {tempPassword}
        </p>
        <p className="mt-4 mb-4">
          <Trans>
            (Password is valid for <strong>2 days</strong>, in the meantime, the
            patient has to change it.)
          </Trans>
        </p>
        <p className="mt-4 mb-4">
          <strong>
            <Trans>
              Save this password somewhere, as it cannot be shown again.
            </Trans>
          </strong>
        </p>
        <Button onClick={() => setTempPassword(null)}>
          <Trans>Close</Trans>
        </Button>
      </Dialog>
    </>
  );
});
